import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { clean } from './api';
import { getFirebase } from './firebase';

export type TicketStatus = 'backlog' | 'todo' | 'in_progress' | 'done';

export const TICKET_STATUSES: readonly TicketStatus[] = [
  'backlog',
  'todo',
  'in_progress',
  'done',
];

export const TICKET_STATUS_LABEL: Record<TicketStatus, string> = {
  backlog: 'Backlog',
  todo: 'To do',
  in_progress: 'In progreSS',
  done: 'Done',
};

// Cap on linked assets per ticket.
export const MAX_ASSETS_PER_TICKET = 10;

// assetKeys entry pointing at a freeform upload (ExampleRecord) instead of a
// catalog asset: 'example:<id>'. Id, not url - a replaced image keeps the link.
export const EXAMPLE_ASSET_PREFIX = 'example:';

export type Ticket = {
  id: string;
  title: string;
  description: string;
  status: TicketStatus;
  // Asset catalog keys the ticket is about (0..MAX_ASSETS_PER_TICKET).
  assetKeys?: string[];
  assignee?: string;
  createdBy?: string;
  createdAtMs?: number;
  // Explicit sprint override (set by "move to next sprint"). When absent, the
  // ticket's sprint is derived from its creation date.
  sprintIndex?: number;
  // manual position inside a column, set on drop; absent = default ordering
  order?: number;
  // when the ticket was moved to done; done column autosorts by it
  doneAtMs?: number;
};

export type TicketInput = {
  title: string;
  description: string;
  assetKeys?: string[];
  assignee?: string;
};

export async function listTickets(): Promise<Ticket[]> {
  const { db } = getFirebase();
  const snap = await getDocs(query(collection(db, 'tickets'), orderBy('createdAt', 'desc')));
  return snap.docs.map((d) => {
    const data = d.data() as Omit<Ticket, 'id' | 'createdAtMs'> & {
      createdAt?: { toMillis?: () => number };
      // Legacy single-asset field from before multi-asset support.
      assetKey?: string;
    };
    const assetKeys = data.assetKeys ?? (data.assetKey ? [data.assetKey] : undefined);
    return { ...data, assetKeys, id: d.id, createdAtMs: data.createdAt?.toMillis?.() };
  });
}

// Live feed of done tickets (just the assignee, which is all the topbar counters
// need). Fires the moment any ticket moves to Done from anywhere, even another
// designer's tab. The counter derives both the team total and the per-user count
// from this one listener. Returns the unsubscribe fn.
export function subscribeDoneTickets(
  onData: (rows: { assignee?: string }[]) => void,
): () => void {
  const { db } = getFirebase();
  const q = query(collection(db, 'tickets'), where('status', '==', 'done'));
  return onSnapshot(
    q,
    (snap) => onData(snap.docs.map((d) => ({ assignee: (d.data() as { assignee?: string }).assignee }))),
    () => {},
  );
}

// Returns the new ticket's id (so callers can attach example uploads to it).
export async function createTicket(input: TicketInput, userEmail: string): Promise<string> {
  const { db } = getFirebase();
  const ref = await addDoc(
    collection(db, 'tickets'),
    clean({
      title: input.title,
      description: input.description,
      status: 'backlog' as TicketStatus,
      assetKeys: input.assetKeys?.length ? input.assetKeys.slice(0, MAX_ASSETS_PER_TICKET) : undefined,
      assignee: input.assignee || undefined,
      createdBy: userEmail,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }),
  );
  return ref.id;
}

export async function updateTicket(
  id: string,
  patch: Partial<
    Pick<
      Ticket,
      | 'status'
      | 'assignee'
      | 'assetKeys'
      | 'title'
      | 'description'
      | 'sprintIndex'
      | 'order'
      | 'doneAtMs'
    >
  >,
): Promise<void> {
  const { db } = getFirebase();
  await updateDoc(doc(db, 'tickets', id), { ...patch, updatedAt: serverTimestamp() });
}

// Admin-only (enforced by firestore.rules). The UI hides delete for non-admins.
export async function deleteTicket(id: string): Promise<void> {
  const { db } = getFirebase();
  await deleteDoc(doc(db, 'tickets', id));
}
